//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2025. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import SwitcherIframe, { getSwitcherPositionForApp } from './SwitcherIframe';
import { useActiveLoopContext } from '../../ActiveLoopContext';
import { useTeamspaceContext } from '../../TeamspaceContext';
import {
  buildUrlWithLoopContext,
  readContextFromUrl,
} from '../../utils/loopContextUtils';

function EmbedSwitcher() {
  const location = useLocation();
  const {
    activeLoopId,
    activeLoopName,
    activeTeamspaceId,
    isLoopContextActive,
    setLoopContext,
    setTeamspaceContext,
  } = useActiveLoopContext();
  const { teamspaceId: teamspaceCtxId } = useTeamspaceContext();
  const [iframeSrc, setIframeSrc] = useState<string | null>(null);
  const [appName, setAppName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [path, setPath] = useState<string | null>(null);

  // Keep context in a ref so the navigation effect always reads fresh values
  // without re-running (and reloading the iframe) on every context change.
  // Live context updates are delivered via postMessage by useContextBridge.
  const contextRef = useRef({
    isLoopContextActive,
    activeLoopId,
    activeLoopName,
    activeTeamspaceId,
    teamspaceCtxId,
    setLoopContext,
    setTeamspaceContext,
  });
  useEffect(() => {
    contextRef.current = {
      isLoopContextActive,
      activeLoopId,
      activeLoopName,
      activeTeamspaceId,
      teamspaceCtxId,
      setLoopContext,
      setTeamspaceContext,
    };
  });

  // Only navigation (location change) drives iframe src recomputation.
  useEffect(() => {
    let ctx = contextRef.current;
    const searchParams = new URLSearchParams(location.search);
    const pathParam = searchParams.get('path');
    const name = searchParams.get('name');

    if (pathParam) {
      let newPath = decodeURIComponent(pathParam);

      // A tab opened from the app switcher starts with an empty
      // ActiveLoopContext (sessionStorage is per tab and the switcher opens
      // tabs with `noopener`). The Loop/Teamspace context travels inside the
      // `path` parameter, so adopt it here BEFORE the iframe is mounted.
      // Otherwise SwitcherIframe would freeze an empty context, strip loopId
      // from the iframe URL and broadcast a teamspace-level context that
      // overwrites the loop the user picked.
      const fromPath = readContextFromUrl(newPath);
      if (fromPath.teamspaceId) {
        if (fromPath.loopId) {
          if (
            ctx.activeLoopId !== fromPath.loopId ||
            ctx.activeTeamspaceId !== fromPath.teamspaceId
          ) {
            ctx.setLoopContext(
              fromPath.loopId,
              fromPath.loopName || fromPath.loopId,
              fromPath.teamspaceId,
            );
          }
        } else if (ctx.activeTeamspaceId !== fromPath.teamspaceId) {
          // The link names the teamspace the user was working in. It must win
          // over this tab's stored default teamspace; otherwise SwitcherIframe
          // freezes the stale default and rewrites the app URL with it.
          ctx.setTeamspaceContext(fromPath.teamspaceId);
        }
        // Use the adopted values for the URL built below.
        ctx = {
          ...ctx,
          isLoopContextActive: !!fromPath.loopId || ctx.isLoopContextActive,
          activeLoopId: fromPath.loopId ?? ctx.activeLoopId,
          activeLoopName: fromPath.loopId
            ? fromPath.loopName || fromPath.loopId
            : ctx.activeLoopName,
          activeTeamspaceId: fromPath.teamspaceId,
        };
      }

      // Always propagate teamspaceId; append loop params only when active
      const effectiveTeamspaceId = ctx.activeTeamspaceId || ctx.teamspaceCtxId;
      if (effectiveTeamspaceId) {
        newPath = buildUrlWithLoopContext(
          newPath,
          effectiveTeamspaceId,
          ctx.isLoopContextActive ? ctx.activeLoopId : null,
          ctx.isLoopContextActive ? ctx.activeLoopName : null,
        );
      }
      const newSrc = `${window.location.origin}${newPath}`;
      setIframeSrc(newSrc);
      setPath(pathParam);
      if (name) {
        const appNameToSet = name.toLowerCase().includes('velocity')
          ? 'measure'
          : name;
        setAppName(appNameToSet);
      } else {
        // Derive appName from the path parameter (e.g., /build/... -> Build)
        const decodedPath = decodeURIComponent(pathParam);
        const pathSegments = decodedPath.split('/').filter(Boolean);
        const appSegment = pathSegments[0] || '';
        // Remove query string if present (e.g., /build/?state=... -> build)
        const cleanSegment = appSegment.split('?')[0];
        const derivedAppName =
          cleanSegment.charAt(0).toUpperCase() + cleanSegment.slice(1);
        setAppName(derivedAppName);
      }
    } else {
      // Fallback: derive appName from the route path when path parameter is missing
      // (e.g., /automation/embed/build -> Build)
      const segment = location.pathname.split('/').pop() || '';
      setAppName(segment.charAt(0).toUpperCase() + segment.slice(1));
      setIframeSrc(null);
      setPath(null);
      setError('Error: Missing path parameter.');
    }
  }, [location]);

  return (
    <>
      {error && (
        <div id="id-embed-switcher-error" data-testid="embed-switcher-error">
          {error}
        </div>
      )}
      {iframeSrc && (
        <SwitcherIframe
          iframeSrc={iframeSrc}
          appName={appName}
          path={path ?? ''}
          switcherPosition={getSwitcherPositionForApp(appName)}
        />
      )}
    </>
  );
}

export default EmbedSwitcher;
